import { c as _c } from "react-compiler-runtime";
import { useLayoutEffect, useState } from 'react';
import styled from 'foundations-theming/utils';
import { CopyIcon } from 'foundations-assets/icons/components/CopyIcon';
import I18n from 'I18n';
import { useCellSelectionSelector, useWidthsSelector } from '../../redux/reduxHooks';
import { getWidths } from '../../resizing/redux/widthsSlice';
import { useCellSelectionConfig, useCopySelectionRef } from '../context/CellSelectionContext';
import { isCellSelectionEnabled } from '../config';
import { getHasSelection, getIsDragging, getActiveRangeBottomRightCoord, getSelectedCellCoords } from '../redux/cellSelectionSlice';
import { getSelectedCells } from '../utils/getSelectedCells';
import { useFillHandleAnchorElement } from '../hooks/useFillHandleAnchorElement';
import { CellActionMenu } from './CellActionMenu';
import { jsx as _jsx } from "react/jsx-runtime";
const Overlay = styled.div.withConfig({
  displayName: "CellActionMenuOverlay__Overlay"
})(["position:absolute;transform:translate(10px,-50%);z-index:2;"]);

/**
 * Renders the floating cell-selection action menu centered on the active
 * selection's fill-handle corner. Always offers a built-in Copy action,
 * followed by any consumer-provided actions from the cell-selection config.
 *
 * The menu is hidden while a drag is in progress and while the fill-handle
 * cell is not in the DOM (unmounted by virtualization). It is rendered as an
 * absolutely-positioned child of the scroll window so overflow clipping at the
 * table edge is handled automatically.
 */
export const CellActionMenuOverlay = t0 => {
  const $ = _c(31);
  const {
    scrollContainerElementRef,
    columns,
    rows,
    getRowId
  } = t0;
  const config = useCellSelectionConfig();
  const copySelectionRef = useCopySelectionRef();
  const hasSelection = useCellSelectionSelector(getHasSelection);
  const isDragging = useCellSelectionSelector(getIsDragging);
  const bottomRightCoord = useCellSelectionSelector(getActiveRangeBottomRightCoord);
  const coords = useCellSelectionSelector(getSelectedCellCoords);
  const widths = useWidthsSelector(getWidths);
  let t1;
  if ($[0] !== bottomRightCoord || $[1] !== scrollContainerElementRef || $[2] !== widths) {
    t1 = {
      coord: bottomRightCoord,
      scrollContainerElementRef,
      widths
    };
    $[0] = bottomRightCoord;
    $[1] = scrollContainerElementRef;
    $[2] = widths;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    anchorElement
  } = useFillHandleAnchorElement(t1);
  const [position, setPosition] = useState(null);
  let t2;
  if ($[4] !== anchorElement || $[5] !== scrollContainerElementRef) {
    t2 = () => {
      const scrollEl = scrollContainerElementRef.current;
      if (!anchorElement || !scrollEl) {
        setPosition(null);
        return;
      }
      const cellRect = anchorElement.getBoundingClientRect();
      const scrollRect = scrollEl.getBoundingClientRect();
      setPosition({
        left: cellRect.right - scrollRect.left + scrollEl.scrollLeft,
        top: cellRect.bottom - scrollRect.top + scrollEl.scrollTop
      });
    };
    $[4] = anchorElement;
    $[5] = scrollContainerElementRef;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== anchorElement || $[8] !== scrollContainerElementRef || $[9] !== widths) {
    t3 = [anchorElement, widths, scrollContainerElementRef];
    $[7] = anchorElement;
    $[8] = scrollContainerElementRef;
    $[9] = widths;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  useLayoutEffect(t2, t3);
  if (!isCellSelectionEnabled(config)) {
    return null;
  }
  if (!hasSelection || isDragging || !anchorElement || !position) {
    return null;
  }
  let t4;
  let t5;
  if ($[11] !== columns || $[12] !== config || $[13] !== coords || $[14] !== copySelectionRef || $[15] !== getRowId || $[16] !== rows) {
    t5 = Symbol.for("react.early_return_sentinel");
    bb0: {
      var _config$actions;
      const cells = getSelectedCells(coords, columns, rows, getRowId);
      if (cells.length === 0) {
        t5 = null;
        break bb0;
      }
      let t6;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = I18n.text("datatable.cellSelection.copy");
        $[19] = t6;
      } else {
        t6 = $[19];
      }
      let t7;
      if ($[20] !== copySelectionRef) {
        t7 = {
          key: "copy",
          label: t6,
          Icon: CopyIcon,
          onClick: () => {
            var _copySelectionRef$cur;
            return (_copySelectionRef$cur = copySelectionRef.current) === null || _copySelectionRef$cur === void 0 ? void 0 : _copySelectionRef$cur.call(copySelectionRef);
          }
        };
        $[20] = copySelectionRef;
        $[21] = t7;
      } else {
        t7 = $[21];
      }
      const copyItem = t7;
      const consumerItems = ((_config$actions = config.actions) !== null && _config$actions !== void 0 ? _config$actions : []).map(action => {
        var _action$getUnavailabl;
        const disabled = action.isAvailable ? !action.isAvailable(cells) : false;
        return {
          key: action.key,
          label: action.label,
          Icon: action.icon,
          disabled,
          tooltip: disabled ? (_action$getUnavailabl = action.getUnavailableReason) === null || _action$getUnavailabl === void 0 ? void 0 : _action$getUnavailabl.call(action, cells) : undefined,
          onClick: () => action.onAction(cells)
        };
      });
      t4 = [copyItem, ...consumerItems];
    }
    $[11] = columns;
    $[12] = config;
    $[13] = coords;
    $[14] = copySelectionRef;
    $[15] = getRowId;
    $[16] = rows;
    $[17] = t4;
    $[18] = t5;
  } else {
    t4 = $[17];
    t5 = $[18];
  }
  if (t5 !== Symbol.for("react.early_return_sentinel")) {
    return t5;
  }
  const items = t4;
  let t6;
  if ($[22] !== position.left || $[23] !== position.top) {
    t6 = {
      left: position.left,
      top: position.top
    };
    $[22] = position.left;
    $[23] = position.top;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  let t7;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = I18n.text("datatable.cellSelection.actionsLabel");
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== items) {
    t8 = /*#__PURE__*/_jsx(CellActionMenu, {
      ariaLabel: t7,
      items: items
    });
    $[26] = items;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== t6 || $[29] !== t8) {
    t9 = /*#__PURE__*/_jsx(Overlay, {
      style: t6,
      children: t8
    });
    $[28] = t6;
    $[29] = t8;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  return t9;
};